import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth, db } from '@src/firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';

const isBrowser = typeof window !== 'undefined';
const DEVICE_ID_KEY = 'myApp_device_id';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Create or retrieve device ID
const getOrCreateDeviceId = () => {
  const id = localStorage.getItem(DEVICE_ID_KEY);
  console.log('[Auth] Loaded deviceId:', id);
  if (id) return id;

  const newId = crypto.randomUUID();
  localStorage.setItem(DEVICE_ID_KEY, newId);
  console.log('[Auth] Generated new deviceId:', newId);
  return newId;
};

// Clear device ID
const clearDeviceId = () => {
  localStorage.removeItem(DEVICE_ID_KEY);
  console.warn('[Auth] Device ID cleared from localStorage');
};

export const AuthProvider = ({ children }) => {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [role, setRole] = useState(null);
  const [deviceId, setDeviceId] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load device ID
  useEffect(() => {
    if (!isBrowser) return;
    const id = getOrCreateDeviceId();
    setDeviceId(id);
  }, []);

  // Firebase auth listener
  useEffect(() => {
    if (!isBrowser || !auth || !deviceId) {
      //setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setUserProfile(null);
        setRole(null);
        setLoading(false);
        return;
      }

      try {
        const userRef = doc(db, 'users', firebaseUser.uid);
        const userDoc = await getDoc(userRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          const storedId = userData.browserId;

          // ✅ Remote forced logout via doLogout
          if (userData.doLogout) {
            await updateDoc(userRef, { doLogout: false });
            clearDeviceId();
            await signOut(auth);
            alert(t('Your session has been reset successfully. Please log in again.'));
            window.location.replace('/login');
            return;
          }

          // ✅ Browser ID mismatch check (non-admin)
          if (userData.role !== 'admin') {
            if (storedId && storedId !== deviceId) {
              console.warn('[Auth] Device mismatch: storedId:', storedId, 'current deviceId:', deviceId);
              // Don't clear the ID — allow user to retry or recover
              await signOut(auth);
              alert(t('Access denied due to device mismatch. Please log in again from your registered device or contact support.'));
              window.location.replace('/login');
              return;
            }

            // Register new device
            if (!storedId) {
              await updateDoc(userRef, {
                browserId: deviceId,
                lastLogin: serverTimestamp(),
              });
              userData.browserId = deviceId;
            }
          }

          // ✅ Authenticated state
          setUserProfile(userData);
          setRole(userData.role);
          setUser(firebaseUser);

          // Update login timestamp
          await updateDoc(userRef, {
            lastLogin: serverTimestamp(),
          });
        }
      } catch (err) {
        console.error('AuthProvider error:', err);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, [deviceId]);

  // 🔁 Poll for remote doLogout every 2 seconds
  useEffect(() => {
    if (!user || !user.uid) return;

    const userRef = doc(db, 'users', user.uid);

    const interval = setInterval(async () => {
      try {
        const snap = await getDoc(userRef);
        const data = snap.data();

        if (data?.doLogout) {
          await updateDoc(userRef, { doLogout: false });
          clearDeviceId();
          await signOut(auth);
          alert(t('Your session was remotely terminated.'));
          window.location.replace('/login');
        }
      } catch (err) {
        console.error('Error polling logout flag:', err);
      }
    }, 300000); // every 2 seconds

    return () => clearInterval(interval);
  }, [user]);

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (err) {
      console.error('Error during logout:', err);
    }
  };

  const value = {
    user,
    userProfile,
    role,
    loading,
    logout,
    deviceId,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
